import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ActualizarEncabezadoFacturaPayload,
  AgregarDetalleFacturaResponse,
  EncabezadoFactura,
  FacturaEncabezado,
  FiltroDetalleFactura,
  FiltroFacturas,
  GuardarDetalleFacturaPayload,
  OperacionFacturaResponse,
  PeriodoFacturacion,
  ResultadoPaginadoDetalleFactura,
  ResultadoPaginadoFacturas,
  TransporteProveedor,
  ValidacionFactura
} from '../../model/servicios-entregados/factura-encabezado.model';

export function buildApiBaseUrl(baseUrl: string): string {
  const trimmed = (baseUrl || '').trim().replace(/\/+$/, '');
  if (trimmed.toLowerCase().endsWith('/api')) {
    return trimmed;
  }
  return `${trimmed}/api`;
}

/**
 * Repositorio: Factura Encabezado
 * 
 * Se comunica con el backend Spring Boot (servicios-ms puerto 8080).
 * Endpoint: /api/servicios/facturas
 * 
 * Legacy: EncabezadoFacturaHandler.java + FacturasNoEnviadasActivity.java
 * 
 * @author GitHub Copilot - Migración GIROP
 */
@Injectable({
  providedIn: 'root'
})
export class FacturaEncabezadoRepository {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = buildApiBaseUrl(environment.backend.baseUrl);
  private readonly baseUrlListado = `${this.apiBaseUrl}/servicios/facturas`;
  private readonly baseUrlNuevaFactura = `${this.apiBaseUrl}/servicios/factura`;

  /**
   * Lista facturas aplicando filtros opcionales y paginación.
   * 
   * Parámetros de consulta:
   * - cdfactura: Código de factura
   * - mes: Mes (1-12)
   * - anio: Año (YYYY)
   * - nmconsecutivoCliente: Consecutivo del cliente
   * - nmconsecutivoConvenio: Consecutivo del convenio
   * - nmconsecutivoOficina: Consecutivo de la oficina
   * - incluirAnuladas: true para incluir anuladas (default: false)
   * - pagina: Número de página (1-based, default: 1)
   * - tamanio: Tamaño de página (default: 10, max: 100)
   * 
   * @param filtro Filtros de búsqueda
   * @param pagina Número de página (1-based)
   * @param tamanio Tamaño de página
   * @returns Observable del resultado paginado
   */
  listarFacturas(
    filtro: FiltroFacturas,
    pagina = 1,
    tamanio = 10
  ): Observable<ResultadoPaginadoFacturas> {
    let params = new HttpParams()
      .set('pagina', pagina.toString())
      .set('tamanio', tamanio.toString());

    // Agregar filtros opcionales solo si tienen valor
    if (filtro.cdfactura) {
      params = params.set('cdfactura', filtro.cdfactura);
    }
    if (filtro.mes !== undefined && filtro.mes !== null) {
      params = params.set('mes', filtro.mes.toString());
    }
    if (filtro.anio !== undefined && filtro.anio !== null) {
      params = params.set('anio', filtro.anio.toString());
    }
    if (filtro.nmconsecutivoCliente) {
      params = params.set('nmconsecutivoCliente', filtro.nmconsecutivoCliente.toString());
    }
    if (filtro.nmconsecutivoConvenio) {
      params = params.set('nmconsecutivoConvenio', filtro.nmconsecutivoConvenio.toString());
    }
    if (filtro.nmconsecutivoOficina) {
      params = params.set('nmconsecutivoOficina', filtro.nmconsecutivoOficina.toString());
    }
    if (filtro.incluirAnuladas) {
      params = params.set('incluirAnuladas', 'true');
    }

    return this.http.get<ResultadoPaginadoFacturas>(this.baseUrlListado, { params });
  }

  async obtenerPeriodo(anio: number, mes: number, oficinaId: number): Promise<PeriodoFacturacion> {
    return firstValueFrom(
      this.http.get<PeriodoFacturacion>(`${this.baseUrlNuevaFactura}/periodos`, {
        params: {
          anio,
          mes,
          oficinaId
        }
      })
    );
  }

  async existeNombre(nombre: string, excludeNmconsecutivo?: number): Promise<boolean> {
    let params = new HttpParams().set('nombre', nombre);
    if (excludeNmconsecutivo && excludeNmconsecutivo > 0) {
      params = params.set('excludeNmconsecutivo', excludeNmconsecutivo.toString());
    }

    return firstValueFrom(
      this.http.get<boolean>(`${this.baseUrlNuevaFactura}/encabezados/existe-nombre`, {
        params
      })
    );
  }

  async obtenerFacturaPorConsecutivo(nmconsecutivo: number): Promise<FacturaEncabezado> {
    return firstValueFrom(this.http.get<FacturaEncabezado>(`${this.baseUrlListado}/${nmconsecutivo}`));
  }

  async actualizarEncabezado(
    nmconsecutivo: number,
    payload: ActualizarEncabezadoFacturaPayload
  ): Promise<FacturaEncabezado> {
    return firstValueFrom(this.http.put<FacturaEncabezado>(`${this.baseUrlListado}/${nmconsecutivo}`, payload));
  }

  async validarPeriodo(payload: {
    nmconsecutivoOficina: number;
    nmconsecutivoConvenio: number;
    cdservicioFacturacion: number;
    anio: number;
    mes: number;
  }): Promise<ValidacionFactura> {
    return firstValueFrom(
      this.http.post<ValidacionFactura>(`${this.baseUrlNuevaFactura}/validar-periodo`, payload)
    );
  }

  async crearEncabezado(payload: EncabezadoFactura): Promise<EncabezadoFactura> {
    return firstValueFrom(
      this.http.post<EncabezadoFactura>(`${this.baseUrlNuevaFactura}/encabezados`, payload)
    );
  }

  eliminarFactura(nmconsecutivo: number): Observable<OperacionFacturaResponse> {
    return this.http.delete<OperacionFacturaResponse>(`${this.baseUrlListado}/${nmconsecutivo}`);
  }

  enviarFacturaCliente(nmconsecutivo: number): Observable<OperacionFacturaResponse> {
    return this.http.post<OperacionFacturaResponse>(`${this.baseUrlListado}/${nmconsecutivo}/enviar-cliente`, {});
  }

  /**
   * Agrega/recalcula los detalles de una factura.
   *
   * Endpoint: POST /api/servicios/factura/encabezados/{nmconsecutivo}/agregar-detalle
   *
   * Legacy: {@code FacturaEncabezadoFacade.agregarDetalleFacEvento}. Cuando el estado
   * es PEN (Pendiente) el label del legacy es "Agregar detalle"; cuando es CALC
   * (Calculada) es "Modificar detalle" y el backend elimina los detalles y recalcula.
   *
   * @returns Objeto con `{ exito, mensaje }` desde el SP legacy.
   */
  agregarDetalle(nmconsecutivo: number): Observable<AgregarDetalleFacturaResponse> {
    return this.http.post<AgregarDetalleFacturaResponse>(
      `${this.baseUrlNuevaFactura}/encabezados/${nmconsecutivo}/agregar-detalle`,
      {}
    );
  }

  listarDetalleFactura(
    nmconsecutivo: number,
    filtro: FiltroDetalleFactura,
    tamanio = 10,
    cursor?: string | null,
    direction: 'NEXT' | 'PREVIOUS' = 'NEXT'
  ): Observable<ResultadoPaginadoDetalleFactura> {
    let params = new HttpParams()
      .set('tamanio', tamanio.toString())
      .set('direction', direction);

    if (cursor && cursor.trim()) {
      params = params.set('cursor', cursor.trim());
    }

    if (filtro.nmconsecutivoCumplimiento !== undefined && filtro.nmconsecutivoCumplimiento !== null) {
      params = params.set('nmconsecutivoCumplimiento', filtro.nmconsecutivoCumplimiento.toString());
    }
    const fechaProgramacion = this.normalizarFechaDetalle(filtro.feprogramacion);
    if (fechaProgramacion) {
      params = params.set('feprogramacion', fechaProgramacion);
    }
    if (filtro.cdmunicipioEntrega) {
      params = params.set('cdmunicipioEntrega', filtro.cdmunicipioEntrega.trim());
    }
    if (filtro.nmconsecutivoAsesor !== undefined && filtro.nmconsecutivoAsesor !== null) {
      params = params.set('nmconsecutivoAsesor', filtro.nmconsecutivoAsesor.toString());
    }
    if (filtro.nmconsecutivoBeneficiario !== undefined && filtro.nmconsecutivoBeneficiario !== null) {
      params = params.set('nmconsecutivoBeneficiario', filtro.nmconsecutivoBeneficiario.toString());
    }
    if (filtro.snpagoTransporteEmpleado) {
      params = params.set('snpagoTransporteEmpleado', filtro.snpagoTransporteEmpleado);
    }

    return this.http.get<ResultadoPaginadoDetalleFactura>(
      `${this.baseUrlListado}/${nmconsecutivo}/detalles`,
      { params }
    );
  }

  private normalizarFechaDetalle(fecha: string | undefined): string | undefined {
    if (!fecha) {
      return undefined;
    }

    const value = fecha.trim();
    if (!value) {
      return undefined;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const onlyDate = value.split('T')[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(onlyDate) ? onlyDate : undefined;
  }

  guardarDetalleFactura(
    nmconsecutivo: number,
    payload: GuardarDetalleFacturaPayload
  ): Observable<OperacionFacturaResponse> {
    return this.http.put<OperacionFacturaResponse>(
      `${this.baseUrlListado}/${nmconsecutivo}/detalles`,
      payload
    );
  }

  listarCatalogoTransporte(): Observable<TransporteProveedor[]> {
    return this.http.get<TransporteProveedor[]>(`${this.baseUrlListado}/detalles/catalogos/transporte`);
  }
}
